/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { Page, Text, View, Document, StyleSheet, Image, Font } from "@react-pdf/renderer";
import Constants from "utilities/Constants";
import Strings from "utilities/StringSelector";
import Utils from "utilities/Utils";

// Register Roboto Font
Font.register({ family: "RobotoRegular", src: `${process.env.PUBLIC_URL}/fonts/roboto-v20-latin-regular.ttf` });
Font.register({ family: "RobotoMedium", src: `${process.env.PUBLIC_URL}/fonts/Roboto-Medium.ttf` });

const UtilsInstance = new Utils();

export class MonitoringReport extends Component {
  constructor(props) {
    super(props);

    this.state = {
      generatingDate: new Date(),
      // Summary
      monitoringFirstResumePage: -1,
      monitoringFirstLiveListPage: -1,
      monitoringFirstLogPage: -1,
      // Summary numbering control
      isFirstResumePage: true,
      isFirstLiveListPage: true,
      isFirstLogPage: true,
    };
  }

  // ------------------------ Utilities -------------------------
  getDateNow = () => {
    return (
      this.state.generatingDate.getDate()
      + "/"
      + (this.state.generatingDate.getMonth() + 1)
      + "/"
      + this.state.generatingDate.getFullYear()
    );
  }

  getRectImage = (isFilled) => {
    if (isFilled) {
      return <Image src={`${process.env.PUBLIC_URL}/images/filled-rect.png`} style={generalInfoStyles.rectangle} />;
    }
    else {
      return <Image src={`${process.env.PUBLIC_URL}/images/empty-rect.png`} style={generalInfoStyles.rectangle} />;
    }
  }

  getDateTimeNow = () => {
    let year = this.state.generatingDate.getFullYear();
    let month = this.state.generatingDate.getMonth() + 1;
    let day = this.state.generatingDate.getDate();
    let hour = this.state.generatingDate.getHours();
    let minutes = this.state.generatingDate.getMinutes();
    let seconds = this.state.generatingDate.getSeconds();

    if (day < 10) { day = "0" + day; }
    if (month < 10) { month = "0" + month; }
    if (hour < 10) { hour = "0" + hour; }
    if (minutes < 10) { minutes = "0" + minutes; }
    if (seconds < 10) { seconds = "0" + seconds; }

    return (day + "/" + month + "/" + year + " " + hour + ":" + minutes + ":" + seconds);
  }

  getBaudRateText = (baudRate) => {
    const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

    let baudRateKbps = (baudRate / 1000);
    let baudRateMbps = (baudRate / 1000000);

    let baudRateKbpsText = baudRateKbps.toString();
    let baudRateMbpsText = baudRateMbps.toString();

    if (lang !== Constants.EN_LANG_KEY) {
      baudRateKbpsText = UtilsInstance.replacePointWithComma(baudRateKbpsText);
      baudRateMbpsText = UtilsInstance.replacePointWithComma(baudRateMbpsText);
    }

    // If the baud rate is greater then 1000 Kbps show it as Mbps
    if (baudRateKbps >= 1000.00) {
      return baudRateMbpsText + " Mbps";
    }
    else {
      return baudRateKbpsText + " Kbps";
    }
  }

  getCycleTimeText = (cycleTime) => {
    const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

    const cycleTimeMs = parseFloat(cycleTime);
    const cycleTimeS = parseFloat(cycleTime) / 1000;

    let cycleTimeMsText = cycleTimeMs.toFixed(2);
    let cycleTimeSText = cycleTimeS.toFixed(2);

    if (lang !== Constants.EN_LANG_KEY) {
      cycleTimeMsText = UtilsInstance.replacePointWithComma(cycleTimeMsText);
      cycleTimeSText = UtilsInstance.replacePointWithComma(cycleTimeSText);
    }

    if (cycleTimeMs >= 1000.00) {
      return cycleTimeSText + " s";
    }
    else {
      return cycleTimeMsText + " ms";
    }
  }

  // ----------------- Header for all the pages -----------------
  getPageHeader = () => {
    return (
      <View fixed>
        <View style={headerStyles.container}>
          <Image src={`${process.env.PUBLIC_URL}/logo_report.png`} style={headerStyles.logo} />
          <Text style={headerStyles.text}>{Strings.HEADER_TEXT}</Text>
        </View>
        <View style={headerStyles.line}></View>
      </View>
    );
  }

  // ----------------- Footer for all the pages -----------------
  getPageFooter = (reportPart) => {
    return (
      <View style={footerStyles.container} fixed>
        <View style={footerStyles.line}></View>
        <View>
          <Text style={footerStyles.text} render={({ pageNumber, totalPages }) => {
            this.setFirstPageNumbers(reportPart, pageNumber);
            return (`${this.getDateNow()}, ${Strings.PAGE} ${pageNumber} / ${totalPages}`);
          }} fixed />
        </View>
      </View>
    );
  }

  setFirstPageNumbers = (reportPart, pageNumber) => {
    if (reportPart === "monitoringResume" && this.state.isFirstResumePage) {
      this.setState({
        monitoringFirstResumePage: pageNumber,
        isFirstResumePage: false
      });
    }
    else if (reportPart === "monitoringLiveList" && this.state.isFirstLiveListPage) {
      this.setState({
        monitoringFirstLiveListPage: pageNumber,
        isFirstLiveListPage: false
      });
    }
    else if (reportPart === "monitoringLog" && this.state.isFirstLogPage) {
      this.setState({
        monitoringFirstLogPage: pageNumber,
        isFirstLogPage: false
      });
    }
  }

  // ----------------- Generate Report --------------------------
  createReport = () => {
    return (
      <Document
        title={Strings.REPORT_EXPORT_NAME}>
        {this.createGeneralInfoPage()}
        {this.createDataInfoPage()}
        {this.createSummaryPage()}
        {this.creteResumePage()}
        {this.createMonitoringLiveListPages()}
        {this.createMonitoringLogPages()}
        {this.createDisclaimerPage()}
      </Document>
    );
  }

  // ----------------- General Information ---------------------
  createGeneralInfoPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.GENERAL_REPORT}</Text>

        <Text style={reusableStyles.subHeaderText}>{Strings.CLIENT_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }} >

          <View style={{ maxWidth: "400pt" }}>
            <Text style={reusableStyles.normalText}>{Strings.PROJECT_NAME} {this.props.userInput.clientProjectName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.NETWORK_NAME} {this.props.userInput.clientNetworkName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.LOCATION} {this.props.userInput.clientLocation}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_NAME} {this.props.userInput.clientCompanyName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.ACCOUNTABLE_NAME} {this.props.userInput.clientAccountableName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_ADDRESS} {this.props.userInput.clientAddress}</Text>
            <Text style={reusableStyles.normalText}>{Strings.COMPANY_CITY} {this.props.userInput.clientCity}</Text>
            <Text style={reusableStyles.lastInfoText}>{Strings.COMPANY_TEL} {this.props.userInput.clientTel}</Text>
          </View>

          <View style={{ maxWidth: "100pt" }}>
            {this.getClientImage()}
          </View>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.EXECUTING_COMPANY_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }} >

          <View style={{ maxWidth: "400pt" }}>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_COMPANY_NAME} {this.props.userInput.executingCompanyName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_ACCOUNTABLE_NAME} {this.props.userInput.executingAccountableName}</Text>
            <Text style={reusableStyles.normalText}>{Strings.EXECUTING_TEL} {this.props.userInput.executingTel}</Text>
            <Text style={reusableStyles.lastInfoText}>{Strings.EXECUTING_EMAIL} {this.props.userInput.executingEmail}</Text>
          </View>

          <View style={{ maxWidth: "100pt" }}>
            {this.getExecutorImage()}
          </View>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.ANNOTATIONS}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={generalInfoStyles.notesSection}>
          <Text style={reusableStyles.normalText}>{this.props.userInput.notes}</Text>
        </View>

        <Text style={reusableStyles.subHeaderText}>{Strings.CONCLUSION}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <View style={generalInfoStyles.conclusionSection}>

          <View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.CONFORM)}
              <Text style={reusableStyles.normalText}>{Strings.CONFORM_STRING}</Text>
            </View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.CONFORM_W_RESTRICTION)}
              <Text style={reusableStyles.normalText}>{Strings.CONFORM_W_RESTRICTION_STRING}</Text>
            </View>

            <View style={generalInfoStyles.rectangleSection}>
              {this.getRectImage(this.props.userInput.conformity === Constants.NON_CONFORM)}
              <Text style={reusableStyles.normalText}>{Strings.NON_CONFORM_STRING}</Text>
            </View>

          </View>

          <View style={generalInfoStyles.signatureLine} />

        </View>

        {this.getPageFooter("")}

      </Page >
    );
  }

  getClientImage = () => {
    // Only try to draw the Image on the pdf if the user has
    // submited a client image
    if (this.props.userInput.clientImg !== "") {
      return (
        <Image style={{
          maxHeight: "100pt",
          width: "100pt"
        }} src={this.props.userInput.clientImg} />
      );
    }
  }

  getExecutorImage = () => {
    // Only try to draw the Image on the pdf if the user has
    // submited a executor image
    if (this.props.userInput.executorImg !== "") {
      return (
        <Image style={{
          maxHeight: "100pt",
          width: "100pt"
        }} src={this.props.userInput.executorImg} />
      );
    }
  }

  // ----------------- Data Information ------------------------
  createDataInfoPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.DATA_REPORT}</Text>

        <Text style={reusableStyles.subHeaderText}>{Strings.MONITORING_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.TAG_NAME_DP}{this.props.tag} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MONITOR_VERSION_DP}{Constants.TS_MONITOR_VERSION} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MONITOR_INIT_DP}{UtilsInstance.getMonitorInitTime(this.props.uptime, UtilsInstance)} </Text>
        <Text style={reusableStyles.normalText}>{Strings.GENERATING_TIME_DP}{this.getDateTimeNow()} </Text>
        <Text style={reusableStyles.normalText}>{Strings.DURATION_SINCE_MON_BEGIN}{UtilsInstance.formatToRelativeTime2(this.props.uptime, UtilsInstance, Strings)} </Text>

        <Text style={reusableStyles.secondSubHeaderText}>{Strings.ETHERNET_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.IP_DP}{this.props.configData.addr} </Text>
        <Text style={reusableStyles.normalText}>{Strings.MASK_DP}{this.props.configData.netmask} </Text>
        <Text style={reusableStyles.normalText}>{Strings.GATEWAY_DP}{this.props.configData.gateway} </Text>

        <Text style={reusableStyles.secondSubHeaderText}>{Strings.WLAN_INFO}</Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text style={reusableStyles.normalText}>{Strings.NETWORK_NAME_DP}{this.props.wifiConfigData.wifiName}</Text>
        <Text style={reusableStyles.normalText}>{Strings.IP_DP}{this.props.wifiConfigData.wifiAddr}</Text>
        <Text style={reusableStyles.normalText}>{Strings.MASK_DP}{this.props.wifiConfigData.wifiNetmask}</Text>
        <Text style={reusableStyles.normalText}>{Strings.GATEWAY_DP}{this.props.wifiConfigData.wifiGateway}</Text>

        {this.getPageFooter("")}

      </Page >
    );
  }

  // ---------------------- Summary ----------------------------
  createSummaryPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <Text style={reusableStyles.pageTitle}>{Strings.SUMMARY}</Text>

        <Text style={reusableStyles.summaryText} render={() => (
          `1. ${Strings.RESUME} ${Strings.RESUME_DOTS} ${this.state.monitoringFirstResumePage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={() => (
          `2. ${Strings.LIVE_LIST} ${Strings.LIVE_LIST_DOTS} ${this.state.monitoringFirstLiveListPage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={() => (
          `3. ${Strings.LOG} ${Strings.MON_LOG_DOTS} ${this.state.monitoringFirstLogPage}`
        )} fixed />

        <Text style={reusableStyles.summaryText} render={({ totalPages }) => (
          `4. ${Strings.DISCLAIMER} ${Strings.DISCLAIMER_DOTS} ${totalPages}`
        )} fixed />

        {this.getPageFooter("")}

      </Page>
    );
  }

  // ----------------- Resumo -----------------
  creteResumePage = () => {
    if (sessionStorage.getItem("isFiberProcessOn") === "true") {
      return (
        <Page
          size="A4"
          orientation="portrait"
          wrap={true}
          style={reusableStyles.page}>

          {this.getPageHeader()}

          <Text style={monitoringLiveListStyles.pageTitle} fixed>{Strings.M_MONITORING_RESUME_PAGE_TITLE}</Text>

          <Text style={reusableStyles.subHeaderText}>{Strings.PROFIBUS_NETWORK_INFO}</Text>

          <View style={reusableStyles.subHeaderLine} />

          <Text style={reusableStyles.normalText}>{Strings.NETWORK_AVAILABILITY_DP}{this.props.availability}</Text>
          <Text style={reusableStyles.normalText}>{Strings.BAUD_RATE_DP}{this.getBaudRateText(this.props.trafficStatistics.baudRate)}</Text>
          <Text style={reusableStyles.normalText}>{Strings.CYCLE_TIME_DP}{this.getCycleTimeText(this.props.trafficStatistics.cycleTime)}</Text>
          <Text style={reusableStyles.normalText}>
            {Strings.WATCHDOG_DP}
            {this.props.trafficStatistics.watchdog > 0 ? this.props.trafficStatistics.watchdog + " ms" : "-"}
          </Text>
          <Text style={reusableStyles.normalText}>
            {Strings.RETRIES_LIMIT_DP}
            {this.props.trafficStatistics.retryLimit > 0 ? this.props.trafficStatistics.retryLimit : "-"}
          </Text>
          <Text style={reusableStyles.normalText}>{Strings.HSA_DP}{this.props.trafficStatistics.hsa}</Text>

          <Text style={reusableStyles.secondSubHeaderText}>{Strings.DEVICE_DATA}</Text>

          <View style={reusableStyles.subHeaderLine} />

          <Text style={reusableStyles.normalText}>{Strings.MASTERS_DP}{this.props.deviceStatistics.masters}</Text>
          <Text style={reusableStyles.normalText}>{Strings.SLAVES_DP}{this.props.deviceStatistics.slaves}</Text>
          <Text style={reusableStyles.normalText}>{Strings.ONLINE_SLAVES_DP}{this.props.deviceStatistics.online}</Text>
          <Text style={reusableStyles.normalText}>{Strings.MISSING_DP}{this.props.deviceStatistics.missing}</Text>
          <Text style={reusableStyles.normalText}>{Strings.RETRIES_DP}{this.props.networkStatistics.retries}</Text>
          <Text style={reusableStyles.normalText}>{Strings.DIAGNOSTIC_DP}{this.props.networkStatistics.diagnostics}</Text>
          <Text style={reusableStyles.normalText}>{Strings.FAULT_DP}{this.props.networkStatistics.failures}</Text>

          <Text style={reusableStyles.secondSubHeaderText}>{Strings.OPTIC_FIBER}</Text>

          <View style={reusableStyles.subHeaderLine} />

          {this.getChannelOpticFiber(2, this.props.fiberChannelActive.FB1.channel2Active)}

          {this.getChannelOpticFiber(3, this.props.fiberChannelActive.FB1.channel3Active)}

          {this.getSignalRelay()}

          {this.getPageFooter("monitoringResume")}

        </Page >
      );
    } else {
      return (
        <Page
          size="A4"
          orientation="portrait"
          wrap={true}
          style={reusableStyles.page}>

          {this.getPageHeader()}

          <Text style={monitoringLiveListStyles.pageTitle} fixed>{Strings.M_MONITORING_RESUME_PAGE_TITLE}</Text>

          <Text style={reusableStyles.subHeaderText}>{Strings.PROFIBUS_NETWORK_INFO}</Text>

          <View style={reusableStyles.subHeaderLine} />

          <Text style={reusableStyles.normalText}>{Strings.NETWORK_AVAILABILITY_DP}{this.props.availability}</Text>
          <Text style={reusableStyles.normalText}>{Strings.BAUD_RATE_DP}{this.getBaudRateText(this.props.trafficStatistics.baudRate)}</Text>
          <Text style={reusableStyles.normalText}>{Strings.CYCLE_TIME_DP}{this.getCycleTimeText(this.props.trafficStatistics.cycleTime)}</Text>
          <Text style={reusableStyles.normalText}>
            {Strings.WATCHDOG_DP}
            {this.props.trafficStatistics.watchdog > 0 ? this.props.trafficStatistics.watchdog + " ms" : "-"}
          </Text>
          <Text style={reusableStyles.normalText}>
            {Strings.RETRIES_LIMIT_DP}
            {this.props.trafficStatistics.retryLimit > 0 ? this.props.trafficStatistics.retryLimit : "-"}
          </Text>
          <Text style={reusableStyles.normalText}>{Strings.HSA_DP}{this.props.trafficStatistics.hsa}</Text>

          <Text style={reusableStyles.secondSubHeaderText}>{Strings.DEVICE_DATA}</Text>

          <View style={reusableStyles.subHeaderLine} />

          <Text style={reusableStyles.normalText}>{Strings.MASTERS_DP}{this.props.deviceStatistics.masters}</Text>
          <Text style={reusableStyles.normalText}>{Strings.SLAVES_DP}{this.props.deviceStatistics.slaves}</Text>
          <Text style={reusableStyles.normalText}>{Strings.ONLINE_SLAVES_DP}{this.props.deviceStatistics.online}</Text>
          <Text style={reusableStyles.normalText}>{Strings.MISSING_DP}{this.props.deviceStatistics.missing}</Text>
          <Text style={reusableStyles.normalText}>{Strings.RETRIES_DP}{this.props.networkStatistics.retries}</Text>
          <Text style={reusableStyles.normalText}>{Strings.DIAGNOSTIC_DP}{this.props.networkStatistics.diagnostics}</Text>
          <Text style={reusableStyles.normalText}>{Strings.FAULT_DP}{this.props.networkStatistics.failures}</Text>

          {this.getPageFooter("monitoringResume")}

        </Page >
      );
    }
  }

  getSignalRelay = () => {
    if (this.props.fiber.FB1.signal && this.props.fiberChannelActive.FB1.signalActive) {
      return (
        <View>
          <Text style={reusableStyles.secondOpticFiberSubHeaderText}>{Strings.RELAY_STATUS}</Text>
          <Text style={reusableStyles.normalText}>{Strings.STATUS}: {Strings.OK}</Text>
        </View>
      );
    }
    else if ((!this.props.fiber.FB1.signal && this.props.fiberChannelActive.FB1.signalActive)) {
      return (
        <View>
          <Text style={reusableStyles.secondOpticFiberSubHeaderText}>{Strings.RELAY_STATUS}</Text>
          <Text style={reusableStyles.normalText}>{Strings.STATUS}: {Strings.FAULT}</Text>
        </View>
      );
    }
  }

  getChannelOpticFiber = (channel, isChannelActive) => {
    if (isChannelActive && channel === 2) {
      return (
        <View>
          <Text style={reusableStyles.secondOpticFiberSubHeaderText}>{Strings.CHANNEL_2}</Text>
          <Text style={reusableStyles.normalText}>{Strings.SIGNAL_QUALITY}: {this.getVoltageValue(this.props.fiber.FB1.channel2)}</Text>
          <Text style={reusableStyles.normalText}>{Strings.STATUS}: {this.createStatusCell(this.props.fiber.FB1.statusChannel2)}</Text>
        </View>
      );
    }
    else if (isChannelActive && channel === 3) {
      return (
        <View>
          <Text style={reusableStyles.secondOpticFiberSubHeaderText}>{Strings.CHANNEL_3}</Text>
          <Text style={reusableStyles.normalText}>{Strings.SIGNAL_QUALITY}: {this.getVoltageValue(this.props.fiber.FB1.channel3)}</Text>
          <Text style={reusableStyles.normalText}>{Strings.STATUS}: {this.createStatusCell(this.props.fiber.FB1.statusChannel3)}</Text>
        </View>
      );
    }
  }

  getVoltageValue = (value) => {
    const voltage = value !== null ? value.toFixed(2).replace(".", ",") : "";
    return voltage.concat(Strings.VOLTAGE);
  }

  createStatusCell = (StatusChannel) => {
    if (StatusChannel === Constants.GOOD) {
      return Strings.GOOD_STRING;
    }
    else if (StatusChannel === Constants.ATTENTION) {
      return Strings.ATTENTION_STRING;
    }
    else if (StatusChannel === Constants.BAD) {
      return Strings.BAD_STRING;
    }
    else {
      return Strings.UNKNOWN_STRING;
    }
  }

  // ----------------- Live List -----------------
  createMonitoringLiveListPages = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}
        {this.getMonitoringLiveListPageTitle()}

        <View style={monitoringLiveListStyles.table}>
          {this.createMonitoringLiveListTableHeader()}
          {this.createMonitoringLiveListTable()}
        </View>

        {this.getPageFooter("monitoringLiveList")}

      </Page>
    );
  }

  getMonitoringLiveListPageTitle = () => {
    return (
      <Text style={monitoringLiveListStyles.pageTitle} fixed>{Strings.M_MONITORING_LIVE_LIST_PAGE_TITLE}</Text>
    );
  }

  createMonitoringLiveListTableHeader = () => {
    return (
      <View style={monitoringLiveListStyles.tableRow} fixed>

        <View style={monitoringLiveListStyles.addressTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.ADDRESS}</Text>
        </View>

        <View style={monitoringLiveListStyles.tagColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.TAG}</Text>
        </View>

        <View style={monitoringLiveListStyles.modelColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.MODEL}</Text>
        </View>

        <View style={monitoringLiveListStyles.diagnosticsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.DIAGNOSTICS_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.retriesFaultsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.RETRIES_NO_FAULT_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.retriesFaultsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.RETRIES_STRING} {Strings.TOTAL}</Text>
        </View>

        <View style={monitoringLiveListStyles.faultsTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.FAULTS_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.watchdogTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.WATCHDOG_STRING}</Text>
        </View>

        <View style={monitoringLiveListStyles.statusTableColHeader}>
          <Text style={monitoringLiveListStyles.tableCellHeader}>{Strings.STATUS}</Text>
        </View>

      </View>
    );
  }

  createMonitoringLiveListTable = () => {
    return (
      this.props.livelist.map(device => (
        <View style={monitoringLiveListStyles.tableRow} key={device.address} wrap={false}>

          <View style={monitoringLiveListStyles.addressTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{device.address}</Text>
          </View>

          <View style={monitoringLiveListStyles.tagTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{device.tag}</Text>
          </View>

          <View style={monitoringLiveListStyles.modelTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getModelText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.diagnosticsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getDiagnosticsText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.retriesFaultsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getRetriesNoFaultText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.retriesFaultsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getRetriesText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.faultsTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getFaultsText(device)}</Text>
          </View>

          <View style={monitoringLiveListStyles.watchdogTableCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{this.getWatchdogText(device)}</Text>
          </View>

          {this.createMonitoringStatusCell(device)}

        </View>
      ))
    );
  }

  getModelText = (device) => {
    if (device.status === "master" || device.status === "masterFault" || device.status === "mpi" || device.status === "mpiFault") {
      return "-";
    }
    else {
      return device.model;
    }
  }

  getDiagnosticsText = (device) => {
    if (device.status === "master" || device.status === "masterFault" || device.status === "mpi" || device.status === "mpiFault") {
      return "-";
    }
    else {
      return device.diagnostics;
    }
  }

  getRetriesText = (device) => {
    if (device.status === "master" || device.status === "masterFault" || device.status === "mpi" || device.status === "mpiFault") {
      return "-";
    }
    else {
      return device.retries;
    }
  }

  getRetriesNoFaultText = (device) => {
    if (device.status === "master" || device.status === "masterFault" || device.status === "mpi" || device.status === "mpiFault") {
      return "-";
    }
    else {
      return device.retriesNoFault;
    }
  }

  getFaultsText = (device) => {
    if (device.status === "master" || device.status === "masterFault") {
      return "-";
    }
    else {
      return device.faults;
    }
  }

  getWatchdogText = (device) => {
    if (device.watchdog === null) {
      return "---";
    }
    else {
      if (device.watchdog) {
        return Strings.ACTIVE;
      }
      else {
        return Strings.INACTIVE;
      }
    }
  }

  createMonitoringStatusCell = (device) => {
    if (device.isInBlacklist) {
      return (
        <View style={monitoringLiveListStyles.blacklistTableStatusCol}>
          <Text style={monitoringLiveListStyles.blacklistTableCell}>{Strings.BLACKLIST_STRING}</Text>
        </View>
      );
    }
    else if (device.isInAutomaticBlacklist) {
      return (
        <View style={monitoringLiveListStyles.automaticBlacklistTableStatusCol}>
          <Text style={monitoringLiveListStyles.blacklistTableCell}>{Strings.CRITIC_SLAVE_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "ok") {
      return (
        <View style={monitoringLiveListStyles.okTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.OK_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "diagnostic") {
      return (
        <View style={monitoringLiveListStyles.diagnosticTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.DIAGNOSTIC_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "online") {
      return (
        <View style={monitoringLiveListStyles.onlineTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.ONLINE_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "fault") {
      return (
        <View style={monitoringLiveListStyles.faultTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.FAULT_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "errorConfig") {
      return (
        <View style={monitoringLiveListStyles.configErrorTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.CONFIG_ERROR_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "errorParam") {
      return (
        <View style={monitoringLiveListStyles.paramErrorTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.PARAM_ERROR_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "master") {
      if (device.masterStatus !== null) {
        return (
          <View style={monitoringLiveListStyles.masterTableStatusCol}>
            <Text style={monitoringLiveListStyles.tableCell}>{Strings.MASTER} {UtilsInstance.getMasterStatusText(device.masterStatus)}</Text>
          </View>
        );
      }
      else {
        return (
          <View style={monitoringLiveListStyles.masterTableStatusCol}>
            <Text style={monitoringLiveListStyles.tableCell}> {Strings.MASTER_CLASS_2} </Text>
          </View>
        );
      }
    }
    else if (device.status === "masterFault") {
      return (
        <View style={monitoringLiveListStyles.faultTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.MASTER_FAULT_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "slaveMasterFault") {
      return (
        <View style={monitoringLiveListStyles.slaveMasterFaultTableStatusCol}>
          <Text style={monitoringLiveListStyles.blacklistTableCell}>{Strings.SLAVE_MASTER_FAULT_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "missing") {
      return (
        <View style={monitoringLiveListStyles.missingTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.MISSING_SINGULAR_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "mpi") {
      return (
        <View style={monitoringLiveListStyles.mpiTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.MPI_STRING}</Text>
        </View>
      );
    }
    else if (device.status === "mpiFault") {
      return (
        <View style={monitoringLiveListStyles.faultTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.MPI_STRING}</Text>
        </View>
      );
    }
    else {
      return (
        <View style={monitoringLiveListStyles.missingTableStatusCol}>
          <Text style={monitoringLiveListStyles.tableCell}>{Strings.UNKNOWN_NAME}</Text>
        </View>
      );
    }
  }

  // ----------------- Log -----------------
  createMonitoringLogPages = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}
        <Text style={monitoringLogStyles.pageTitle} fixed>{Strings.M_MONITORING_LOG_PAGE_TITLE}</Text>
        <Text style={monitoringLogStyles.pageLastEventsText} fixed>{Strings.LAST_200_EVENTS}</Text>

        <View style={monitoringLogStyles.table}>
          {this.createMonitoringLogTableHeader()}
          {this.createMonitoringLogTable()}
        </View>

        {this.getPageFooter("monitoringLog")}

      </Page>
    );
  }

  createMonitoringLogTableHeader = () => {
    return (
      <View style={monitoringLogStyles.tableRow} fixed>

        <View style={monitoringLogStyles.horaryTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.HORARY}</Text>
        </View>

        <View style={monitoringLogStyles.addressTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.ADDRESS}</Text>
        </View>

        <View style={monitoringLogStyles.modelTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.TAG}</Text>
        </View>

        <View style={monitoringLogStyles.descriptionTableColHeader}>
          <Text style={monitoringLogStyles.tableCellHeader}>{Strings.DESCRIPTION}</Text>
        </View>

      </View>
    );
  }

  createMonitoringLogTable = () => {
    let last200Events = [];

    if (this.props.log.length > Constants.MAX_NUM_OF_LOG_EVENTS) {
      for (let i = 0; i < Constants.MAX_NUM_OF_LOG_EVENTS; i++) {
        last200Events.push(this.props.log[i]);
      }
    }
    else {
      for (let i = 0; i < this.props.log.length; i++) {
        last200Events.push(this.props.log[i]);
      }
    }

    return (
      last200Events.map(logEntry => (
        <View style={monitoringLogStyles.tableRow} key={logEntry.event} wrap={true}>

          <View style={monitoringLogStyles.horaryTableCol}>
            <Text style={monitoringLogStyles.tableCell}>{this.getMonitoringLogTime(logEntry)}</Text>
          </View>

          <View style={monitoringLogStyles.addressTableCol}>
            <Text style={monitoringLogStyles.tableCell}>{(logEntry.address === "128") ? "" : logEntry.address}</Text>
          </View>

          <View style={monitoringLogStyles.modelTableCol}>
            <Text style={monitoringLogStyles.tableCell}>{this.getLogTag(logEntry)}</Text>
          </View>

          {this.getMonitoringLogDescription(logEntry)}

        </View>
      ))
    );
  }

  formatTime = (eventTimeInSeconds) => {
    if (eventTimeInSeconds !== undefined && eventTimeInSeconds !== null) {
      const eventTimeInMillis = eventTimeInSeconds * 1000;
      const uptimeInMillis = this.props.uptime * 1000;
      const timeNow = new Date().getTime();
      const time = timeNow - uptimeInMillis + eventTimeInMillis;
      const dateToShow = new Date(time);
      return (dateToShow);
    }
    else {
      return Strings.BEFORE_MONITOR_INIT_STR;
    }
  }

  getMonitoringLogTime = (logEntry) => {
    if (logEntry.ntp !== null && logEntry.ntp === true) {
      let parsedMillis = Date.parse(logEntry.time);
      let dateToShow = new Date(parsedMillis);

      let year = dateToShow.getFullYear();
      let month = dateToShow.getMonth() + 1;
      let day = dateToShow.getDate();
      let hour = dateToShow.getHours();
      let minutes = dateToShow.getMinutes();

      if (day < 10) { day = "0" + day; }
      if (month < 10) { month = "0" + month; }
      if (hour < 10) { hour = "0" + hour; }
      if (minutes < 10) { minutes = "0" + minutes; }

      return day + "/" + month + "/" + year + " " + hour + ":" + minutes;
    }
    else {
      if (logEntry.time === null) {
        return Strings.BEFORE_MONITOR_INIT_STR;
      }
      else {
        let dateToShow = this.formatTime(logEntry.time);
        let year = dateToShow.getFullYear();
        let month = dateToShow.getMonth() + 1;
        let day = dateToShow.getDate();
        let hour = dateToShow.getHours();
        let minutes = dateToShow.getMinutes();

        if (day < 10) { day = "0" + day; }
        if (month < 10) { month = "0" + month; }
        if (hour < 10) { hour = "0" + hour; }
        if (minutes < 10) { minutes = "0" + minutes; }

        return day + "/" + month + "/" + year + " " + hour + ":" + minutes;
      }
    }

  }

  getLogTag = (logEntry) => {
    if (logEntry.tag === "") {
      return "-";
    }
    else if (logEntry.tag === Strings.NETWORK_EVENT) {
      return "";
    }
    else {
      return logEntry.tag;
    }
  }

  getMonitoringLogDescription = (logEntry) => {
    if (logEntry.tipo === Constants.DIAGNOSTIC_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>{Strings.SLAVE_DIAGNOSTIC}</Text>
          {
            logEntry.description.map(description => (
              <Text key={description} style={monitoringLogStyles.logDescriptionTableCell}>{"• " + description}</Text>
            ))
          }
        </View>
      );
    }
    else if (logEntry.tipo === Constants.MONITOR_INIT_LOG_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          {
            logEntry.description.map(description => (
              <Text key={description} style={monitoringLogStyles.logDescriptionTableCell}>{description}</Text>
            ))
          }
        </View>
      );
    }
    else if (logEntry.tipo === Constants.OK_EVENT_TYPE) {
      return (
        <View style={monitoringLogStyles.descriptionTableCol}>
          {
            logEntry.description.map(description => (
              <Text key={description} style={monitoringLogStyles.logDescriptionTableCell}>{"• " + description}</Text>
            ))
          }
        </View>
      );
    }
    else {
      if (logEntry.address === "128") {
        return (
          <View style={monitoringLogStyles.descriptionTableCol}>
            {
              logEntry.description.map(description => (
                <React.Fragment key={description}>
                  <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>
                    {Strings.NETWORK_EVENT}:
                  </Text>
                  <Text style={monitoringLogStyles.logDescriptionTableCellTitle}>
                    {Strings.STATUS_CHANGE}
                    <Text style={monitoringLogStyles.logDescriptionTableCell}>{description}</Text>
                  </Text>
                </React.Fragment>
              ))
            }
          </View>
        );
      }
      else {
        return (
          <View style={monitoringLogStyles.descriptionTableCol}>
            {
              logEntry.description.map(description => (
                <Text key={description} style={monitoringLogStyles.logDescriptionTableCellTitle}>
                  {Strings.STATUS_CHANGE}
                  <Text style={monitoringLogStyles.logDescriptionTableCell}>{description}</Text>
                </Text>
              ))
            }
          </View>
        );
      }
    }
  }

  // ----------------- Disclaimer -----------------
  createDisclaimerPage = () => {
    return (
      <Page
        size="A4"
        orientation="portrait"
        wrap={true}
        style={reusableStyles.page}>

        {this.getPageHeader()}

        <View style={{ marginTop: "30vh" }} />

        <Text style={reusableStyles.pageTitle}>{Strings.END_OF_REPORT}</Text>

        <View style={{ marginTop: "23vh" }} />

        <Text style={{
          fontSize: 14,
          fontFamily: "RobotoMedium",
          marginBottom: "5px"
        }}>
          {Strings.DISCLAIMER}
        </Text>

        <View style={reusableStyles.subHeaderLine} />

        <Text
          style={{
            fontSize: 12,
            marginBottom: 2,
            fontFamily: "RobotoRegular",
            textAlign: "justify"
          }}>
          {Strings.DISCLAIMER_TEXT}
        </Text>

        {this.getPageFooter("")}

      </Page >
    );
  }

  render() {
    return (
      this.createReport()
    );
  }
}

const reusableStyles = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingHorizontal: 40,
    paddingBottom: 56
  },

  pageTitle: {
    textAlign: "center",
    fontSize: 18,
    marginTop: "2vh",
    marginBottom: "4vh",
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  subHeaderText: {
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  secondSubHeaderText: {
    marginTop: "3vh",
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  secondOpticFiberSubHeaderText: {
    marginTop: "1vh",
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  subHeaderLine: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "0.5vh",
    marginBottom: "2vh"
  },

  normalText: {
    fontSize: 12,
    marginBottom: 2,
    fontFamily: "RobotoRegular"
  },

  lastInfoText: {
    fontSize: 12,
    marginBottom: "2vh",
    fontFamily: "RobotoRegular"
  },

  summaryText: {
    fontSize: 12,
    marginBottom: "1vh",
    fontFamily: "RobotoRegular"
  }
});

const headerStyles = StyleSheet.create({
  container: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },

  logo: {
    width: "92pt",
    height: "30pt",
    marginBottom: "6pt"
  },

  text: {
    fontSize: 12,
    fontFamily: "RobotoMedium"
  },

  line: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "1vh",
    marginBottom: "2vh"
  }
});

const footerStyles = StyleSheet.create({
  container: {
    textAlign: "right",
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40
  },

  line: {
    height: "1px",
    backgroundColor: "black",
    marginTop: "1vh",
    marginBottom: "1vh"
  },

  text: {
    fontSize: 12,
    fontFamily: "RobotoRegular"
  }
});

const generalInfoStyles = StyleSheet.create({
  notesSection: {
    maxWidth: "510pt",
    width: "90vw",
    height: "14vh"
  },

  conclusionSection: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: "16vh"
  },

  rectangleSection: {
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center"
  },

  rectangle: {
    width: 28,
    height: 28,
    margin: "1vh vw"
  },
  signatureLine: {
    height: "1px",
    width: "40vw",
    backgroundColor: "black",
  }
});

const monitoringLiveListStyles = StyleSheet.create({
  pageTitle: {
    fontSize: 18,
    marginTop: "2vh",
    marginBottom: "4vh",
    fontFamily: "RobotoMedium"
  },

  table: {
    display: "table",
    width: "auto"
  },

  tableRow: {
    flexDirection: "row"
  },

  addressTableColHeader: {
    width: "9%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    backgroundColor: "#bdbdbd"
  },

  retriesFaultsTableColHeader: {
    width: "11%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  faultsTableColHeader: {
    width: "7%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  watchdogTableColHeader: {
    width: "9%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  diagnosticsTableColHeader: {
    width: "11%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tagColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  modelColHeader: {
    width: "13%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  statusTableColHeader: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tableCellHeader: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  tableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  blacklistTableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    color: "white",
    fontFamily: "RobotoRegular"
  },

  addressTableCol: {
    width: "9%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderTopWidth: 0
  },

  retriesFaultsTableCol: {
    width: "11%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  watchdogTableCol: {
    width: "9%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  faultsTableCol: {
    width: "7%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },


  diagnosticsTableCol: {
    width: "11%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  tagTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  modelTableCol: {
    width: "13%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  blacklistTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "black"
  },

  automaticBlacklistTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#8B0000"
  },

  okTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "lime"
  },

  diagnosticTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "yellow"
  },

  onlineTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#ADD8E6"
  },

  faultTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "red"
  },

  configErrorTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#FFB6C1"
  },

  paramErrorTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#C71585"
  },

  masterTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "white"
  },

  slaveMasterFaultTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#B22222"
  },

  mpiTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    color: "white",
    backgroundColor: "navy"
  },

  missingTableStatusCol: {
    width: "14%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#708090"
  },
});

const monitoringLogStyles = StyleSheet.create({
  pageTitle: {
    fontSize: 18,
    marginTop: "2vh",
    fontFamily: "RobotoMedium"
  },

  pageLastEventsText: {
    fontSize: 12,
    marginBottom: "3vh",
    fontFamily: "RobotoRegular"
  },

  table: {
    display: "table",
    width: "auto"
  },

  tableRow: {
    flexDirection: "row"
  },

  horaryTableColHeader: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    backgroundColor: "#bdbdbd"
  },

  addressTableColHeader: {
    width: "10%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  modelTableColHeader: {
    width: "25%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  descriptionTableColHeader: {
    width: "50%",
    borderStyle: "solid",
    borderColor: "#000",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    backgroundColor: "#bdbdbd"
  },

  tableCellHeader: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "RobotoMedium"
  },

  horaryTableCol: {
    width: "15%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderTopWidth: 0
  },

  addressTableCol: {
    width: "10%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  modelTableCol: {
    width: "25%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  descriptionTableCol: {
    width: "50%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0
  },

  tableCell: {
    textAlign: "center",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  logDescriptionTableCell: {
    textAlign: "left",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoRegular"
  },

  logDescriptionTableCellTitle: {
    textAlign: "left",
    margin: 4,
    fontSize: 8,
    fontFamily: "RobotoMedium"
  }

});

export default MonitoringReport;
